import PageHeading from "./ui/page-heading";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Check,
  Copy,
  ExternalLink,
  Key,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  User,
} from "lucide-react";

import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { APIKey, OrganizationSummary } from "../shared/Interfaces";
import { OrganizationPermission, hasPermission } from "../shared/organizationPermissions";
import { parseDate } from "../shared/Utils";
import { copyToClipboard } from "../utils/clipboard";
import Snippet from "./Code/Snippet";

import { Button } from "./ui/button";
import {
  Card,
  CardContent,
} from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";
import { reportHandledError, reportApiError } from "../utils/reportError";

const PERIOD_LABELS: Record<string, string> = {
  daily: "Diário",
  weekly: "Semanal",
  monthly: "Mensal",
};

const formatSpendingLabel = (key: APIKey): { spent: string; limit: string } => {
  const spent = key.period_spent ?? 0;
  const limit = key.spending_limit;
  return {
    spent: `R$ ${spent.toFixed(2)}`,
    limit: limit != null ? `R$ ${limit.toFixed(2)}` : "Ilimitado",
  };
};

const isOverLimit = (key: APIKey): boolean => {
  if (key.spending_limit === null || key.spending_limit === undefined) return false;
  return (key.period_spent ?? 0) >= key.spending_limit;
};

function APIKeys() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newKey, setNewKey] = useState<string>("");
  const [createdNewKeyName, setCreatedNewKeyName] = useState<string>("");
  const [keyToDelete, setKeyToDelete] = useState<APIKey | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [scope, setScope] = useState<"personal" | "organization">("personal");
  const [activeOrganizationId, setActiveOrganizationId] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showAllOrgKeys, setShowAllOrgKeys] = useState<boolean>(false);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [newKeyLimit, setNewKeyLimit] = useState<string>("");
  const [newKeyPeriod, setNewKeyPeriod] = useState<string>("monthly");
  const [keyToEdit, setKeyToEdit] = useState<APIKey | undefined>();
  const [editLimit, setEditLimit] = useState<string>("");
  const [editPeriod, setEditPeriod] = useState<string>("monthly");
  const [isSavingLimit, setIsSavingLimit] = useState<boolean>(false);

  const canViewAllOrgKeys =
    scope === "organization" &&
    hasPermission(userPermissions, OrganizationPermission.API_KEYS_VIEW_ALL);
  const canToggleViewAll = canViewAllOrgKeys;

  const fetchOrganizations = useCallback(async () => {
    try {
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data["organizations"] ?? []);
      } else {
        reportApiError(response, { feature: "apikeys.organizations", method: "GET" });
      }
    } catch (e) {
      reportHandledError(e, { category: "api", feature: "apikeys.organizations" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    }
  }, [getAccessTokenSilently]);

  const fetchKeys = useCallback(
    async (organizationId?: string) => {
      setLoading(true);
      try {
        const url = organizationId
          ? `${API_BASE}/api/organizations/${organizationId}/keys/list${
              showAllOrgKeys && canViewAllOrgKeys ? "?all=true" : ""
            }`
          : `${API_BASE}/api/keys/list`;
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Auth ${access_token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setKeys(data["api_keys"]);
          setUserPermissions(
            Array.isArray(data["user_permissions"]) ? data["user_permissions"] : []
          );
        } else {
          reportApiError(response, { feature: "apikeys.list", method: "GET" });
          toast.error("Erro ao buscar chaves de API.", TOASTPROPS);
        }
      } catch (e) {
        reportHandledError(e, { category: "api", feature: "apikeys.list" });
        toast.error("Erro ao buscar chaves de API.", TOASTPROPS);
      }
      setLoading(false);
    },
    [getAccessTokenSilently, showAllOrgKeys, canViewAllOrgKeys]
  );

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (!canViewAllOrgKeys && showAllOrgKeys) setShowAllOrgKeys(false);
  }, [canViewAllOrgKeys, showAllOrgKeys]);

  useEffect(() => {
    if (scope === "personal") {
      fetchKeys();
      setUserPermissions([]);
    } else if (activeOrganizationId) {
      fetchKeys(activeOrganizationId);
    } else {
      setKeys([]);
      setUserPermissions([]);
    }
  }, [scope, activeOrganizationId, fetchKeys, showAllOrgKeys]);

  useEffect(() => {
    if (scope === "organization") {
      if (!organizations.length) {
        setScope("personal");
        setActiveOrganizationId("");
      } else if (!organizations.some((o) => o.organization_id === activeOrganizationId)) {
        setActiveOrganizationId(organizations[0].organization_id);
      }
    }
  }, [organizations, scope, activeOrganizationId]);

  const handleCloseDialog = () => {
    setCreatedNewKeyName("");
    setIsOpen(false);
  };

  const handleOpenDialog = () => {
    if (scope === "organization" && !activeOrganizationId) {
      toast.error("Selecione uma organização para criar chaves de API.", TOASTPROPS);
      return;
    }
    const randomName = "chave_" + Math.random().toString(36).substring(2, 12);
    setCreatedNewKeyName("");
    setNewKey(randomName);
    setNewKeyLimit("");
    setNewKeyPeriod("monthly");
    setIsOpen(true);
  };

  const handleCreateNewKey = async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Informe um nome para a chave de API.", TOASTPROPS);
      return;
    }
    const isOrg = scope === "organization";
    const orgId = isOrg ? activeOrganizationId : undefined;
    if (isOrg && !orgId) {
      toast.error("Selecione uma organização para criar chaves de API.", TOASTPROPS);
      return;
    }
    const baseUrl = isOrg
      ? `${API_BASE}/api/organizations/${orgId}/keys/create`
      : `${API_BASE}/api/keys/create`;
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    try {
      setIsCreating(true);
      const limitParam = newKeyLimit
        ? `&spending_limit=${parseFloat(newKeyLimit)}&spending_limit_period=${newKeyPeriod}`
        : "";
      const response = await fetch(
        `${baseUrl}?name=${encodeURIComponent(trimmedName)}${limitParam}`,
        { method: "POST", headers: { Authorization: `Auth ${access_token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const createdKey = data["api_key"];
        const copied = await copyToClipboard(createdKey["key"]);
        if (!copied) {
          toast.warning(
            "Chave criada, mas não foi possível copiá-la automaticamente. Copie manualmente abaixo.",
            TOASTPROPS,
          );
        }
        setCreatedNewKeyName(createdKey["name"]);
        setKeys((prev) => [...prev, createdKey]);
        setNewKey(createdKey["key"]);
      } else {
        reportApiError(response, { feature: "apikeys.create", method: "POST" });
        const errorMessage = await response.text();
        const parsed = JSON.parse(errorMessage);
        toast.error(parsed.detail, TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "apikeys.create" });
      toast.error("Erro ao criar chave de API.", TOASTPROPS);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyNewKey = async (key: string) => {
    const copied = await copyToClipboard(key);
    if (copied) {
      toast.success("Chave copiada para a área de transferência.", TOASTPROPS);
    } else {
      toast.error(
        "Não foi possível copiar. Selecione o texto e copie manualmente.",
        TOASTPROPS,
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (keyToDelete) {
      await handleDeleteKey(keyToDelete);
      setKeyToDelete(undefined);
    }
  };

  const handleDeleteKey = async (key: APIKey) => {
    const isOrg = scope === "organization";
    const orgId = isOrg ? activeOrganizationId : undefined;
    if (isOrg && !orgId) {
      toast.error("Selecione uma organização para gerenciar chaves.", TOASTPROPS);
      return;
    }
    const url = isOrg
      ? `${API_BASE}/api/organizations/${orgId}/keys/delete`
      : `${API_BASE}/api/keys/delete`;
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    setIsDeleting(true);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Auth ${access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ api_key_name: key.name }),
      });
      if (response.ok) {
        setKeys((prev) => prev.filter((k) => k.name !== key.name));
        toast.success("Chave de API apagada com sucesso.", TOASTPROPS);
      } else {
        reportApiError(response, { feature: "apikeys.delete", method: "POST" });
        toast.error("Erro ao apagar chave de API.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "apikeys.delete" });
      toast.error("Erro ao apagar chave de API.", TOASTPROPS);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenEditLimit = (key: APIKey) => {
    setKeyToEdit(key);
    setEditLimit(key.spending_limit != null ? String(key.spending_limit) : "");
    setEditPeriod(key.spending_limit_period || "monthly");
  };

  const handleUpdateLimit = async () => {
    if (!keyToEdit) return;
    const url =
      scope === "organization"
        ? `${API_BASE}/api/organizations/${activeOrganizationId}/keys/update`
        : `${API_BASE}/api/keys/update`;
    const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
    if (!access_token) return;
    setIsSavingLimit(true);
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: { Authorization: `Auth ${access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key_name: keyToEdit.name,
          spending_limit: editLimit ? parseFloat(editLimit) : null,
          spending_limit_period: editPeriod,
        }),
      });
      if (response.ok) {
        fetchKeys(scope === "organization" ? activeOrganizationId : undefined);
        setKeyToEdit(undefined);
        toast.success("Limite atualizado com sucesso.", TOASTPROPS);
      } else {
        reportApiError(response, { feature: "apikeys.update", method: "PATCH" });
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Erro ao atualizar limite.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "apikeys.update" });
      toast.error("Erro ao atualizar limite.", TOASTPROPS);
    } finally {
      setIsSavingLimit(false);
    }
  };

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setScope("personal");
      setActiveOrganizationId("");
    } else {
      setScope("organization");
      setActiveOrganizationId(value);
    }
    if (isOpen) handleCloseDialog();
  };

  const scopeSelectValue = scope === "personal" ? "personal" : activeOrganizationId;
  const createButtonDisabled = scope === "organization" && !activeOrganizationId;
  const hasOrganizations = organizations.length > 0;
  const showToggleButton = scope === "organization" && canToggleViewAll;
  const showOwnerColumn = scope === "organization" && showAllOrgKeys && canViewAllOrgKeys;

  return (
    <div className="essential-keys flex flex-col gap-6 pb-12">
      <PageHeading title="Chaves de API" description="Gerencie as credenciais que dão acesso à API da Maritaca.">
            <Button
              size="default"
              onClick={handleOpenDialog}
              disabled={createButtonDisabled}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Criar Nova Chave
            </Button>
      </PageHeading>
      <div className="essential-key-toolbar">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-sm text-muted-foreground">Visualizar chaves:</Label>
              <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
                <SelectTrigger className="w-full sm:w-[280px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Minhas chaves pessoais
                    </span>
                  </SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.organization_id} value={org.organization_id}>
                      <span className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Org: {org.name ?? org.organization_id}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showToggleButton && (
                <Button
                  variant={showAllOrgKeys ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (!canViewAllOrgKeys) {
                      toast.error(
                        "Você não tem permissão para visualizar todas as chaves.",
                        TOASTPROPS
                      );
                      return;
                    }
                    setShowAllOrgKeys((prev) => !prev);
                  }}
                >
                  {showAllOrgKeys ? "Ver Apenas Minhas Chaves" : "Ver Todas as Chaves da Org"}
                </Button>
              )}
            </div>
          </div>

      </div>

      <Card className="essential-key-table overflow-hidden">
        <div className="overflow-x-auto" role="region" aria-label="Lista de chaves de API" tabIndex={0}>
          <table className="w-full min-w-[1200px] table-fixed text-sm">
            <colgroup>{(showOwnerColumn ? [14, 20, 16, 13, 13, 14, 10] : [18, 24, 15, 15, 18, 10]).map((width, i) => <col key={i} style={{ width: width + "%" }} />)}</colgroup>
            <thead>
              <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                <th className="px-5 py-3">Nome</th>
                <th className="px-5 py-3">Chave de API</th>
                {showOwnerColumn && <th className="px-5 py-3">Proprietário</th>}
                <th className="px-5 py-3">Data de Criação</th>
                <th className="px-5 py-3">Última Vez Utilizada</th>
                <th className="px-5 py-3">Limite de Gasto</th>
                <th className="px-5 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-32" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-44" /></td>
                    {showOwnerColumn && (
                      <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-40" /></td>
                    )}
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-24" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-24" /></td>
                    <td className="px-5 py-4"><Skeleton className="max-w-full h-4 w-36" /></td>
                    <td className="px-5 py-4 text-right"><Skeleton className="ml-auto h-8 w-16" /></td>
                  </tr>
                ))
              ) : (
                <AnimatePresence initial={false}>
                  {keys.map((key) => (
                    <motion.tr
                      key={key.name}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.15 }}
                      className="border-b border-border/60 transition-colors hover:bg-muted/40"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <KeyRound className="h-3.5 w-3.5" />
                          </span>
                          <span className="font-medium text-foreground">{key.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <code className="rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground/80">
                          {key.key}
                        </code>
                      </td>
                      {showOwnerColumn && (
                        <td className="px-5 py-4 text-muted-foreground">
                          {key.owner_email || "-"}
                        </td>
                      )}
                      <td className="px-5 py-4 text-muted-foreground">
                        {key.created_at ? parseDate(key.created_at) : "-"}
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {key.last_time_used ? parseDate(key.last_time_used) : "Nunca utilizada"}
                      </td>
                      <td className="px-5 py-4">
                        <div
                          className={cn(
                            "flex flex-col gap-0.5 text-xs font-semibold",
                            isOverLimit(key) ? "text-destructive" : "text-foreground"
                          )}
                        >
                          <span>
                            <span className="font-medium text-muted-foreground">
                              Gasto ({PERIOD_LABELS[key.spending_limit_period || "monthly"].toLowerCase()}):{" "}
                            </span>
                            {formatSpendingLabel(key).spent}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="font-medium text-muted-foreground">Limite:</span>
                            {formatSpendingLabel(key).limit}
                            {key.spending_limit != null && (
                              <Badge variant="secondary" className="font-normal">
                                {PERIOD_LABELS[key.spending_limit_period || "monthly"]}
                              </Badge>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleOpenEditLimit(key)}
                            title="Editar limite de gasto"
                            aria-label="Editar limite"
                            data-testid="EditIcon"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setKeyToDelete(key)}
                            title="Excluir chave"
                            aria-label="Excluir chave"
                            data-testid="DeleteIcon"
                            className="hover:bg-destructive/10 hover:border-destructive/40 hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              )}
            </tbody>
          </table>
        </div>
        {!loading && keys.length === 0 && (
          <div className="flex h-[195px] items-center justify-center px-5 py-8 text-center">
            <div className="mx-auto flex max-w-sm flex-col items-center gap-2">
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                        <Key className="h-6 w-6" />
                      </span>
                      <p className="font-semibold text-foreground">Nenhuma chave encontrada</p>
                      <p className="text-sm text-muted-foreground">
                        {scope === "organization"
                          ? "Nenhuma chave vinculada a esta organização ainda."
                          : "Crie sua primeira chave de API para começar."}
                      </p>
                    </div>
          </div>
        )}
      </Card>
          {!hasOrganizations && (
            <p className="mt-3 text-xs text-muted-foreground">Crie uma organização para gerar chaves compartilhadas.</p>
          )}
      <section className="essential-key-quickstart">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Comece a usar a API</h2>
            <p className="mt-1 text-xs text-muted-foreground">Trate suas chaves como senhas. Não as compartilhe.</p>
          </div>
          <a href="https://docs.maritaca.ai/pt/api/comeco-rapido" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            Guia de início rápido <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <details className="essential-code-disclosure">
          <summary><span>Ver exemplo de integração</span><span className="essential-code-format">cURL</span></summary>
          <div className="essential-code-body"><Snippet /></div>
        </details>
      </section>


      {/* Create Modal */}
      <Dialog open={isOpen} onOpenChange={(o) => !o && handleCloseDialog()}>
        <DialogContent translate="no" className="notranslate">
          <DialogHeader>
            <DialogTitle>Criar chave de API</DialogTitle>
            {createdNewKeyName === "" && (
              <DialogDescription>
                Escolha um nome único para sua chave e, opcionalmente, defina um limite de gastos.
              </DialogDescription>
            )}
          </DialogHeader>

          {createdNewKeyName !== "" ? (
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4"
              >
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-foreground">
                  <Check className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Chave criada com sucesso!</p>
                  <p className="text-sm text-muted-foreground">Nome: {createdNewKeyName}</p>
                </div>
              </motion.div>
              <Input value={newKey} disabled className="font-mono" />
              <Button
                variant="default"
                className="w-full gap-2"
                onClick={() => handleCopyNewKey(newKey)}
              >
                <Copy className="h-4 w-4" />
                Copiar Chave
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-key-name">Nome da chave</Label>
                <Input
                  id="new-key-name"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="Digite o nome da chave..."
                  disabled={isCreating}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Limite de gastos (opcional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={newKeyLimit}
                    onChange={(e) => setNewKeyLimit(e.target.value)}
                    placeholder="Sem limite (R$)"
                    min="0"
                    step="0.01"
                    disabled={isCreating}
                    className="flex-1"
                  />
                  <Select value={newKeyPeriod} onValueChange={setNewKeyPeriod} disabled={isCreating}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diário</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog} disabled={isCreating}>Cancelar</Button>
                <Button
                  onClick={() => handleCreateNewKey(newKey)}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    "Criar"
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={keyToDelete !== undefined} onOpenChange={(o) => !o && setKeyToDelete(undefined)}>
        <DialogContent translate="no" className="notranslate">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. Tem certeza que deseja apagar a chave{" "}
              <strong className="text-foreground">{keyToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <Trash2 className="h-4 w-4" />
            </div>
            <p className="text-sm text-foreground/80">
              Quaisquer aplicações usando essa chave deixarão de funcionar imediatamente.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setKeyToDelete(undefined)}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="flex-1"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Limit Modal */}
      <Dialog open={keyToEdit !== undefined} onOpenChange={(o) => !o && setKeyToEdit(undefined)}>
        <DialogContent translate="no" className="notranslate">
          <DialogHeader>
            <DialogTitle>Editar Limite de Gastos</DialogTitle>
            <DialogDescription>
              Defina o limite de gastos em R$ para a chave{" "}
              <strong className="text-foreground">{keyToEdit?.name}</strong>. Deixe vazio para
              remover o limite.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Limite e período</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={editLimit}
                onChange={(e) => setEditLimit(e.target.value)}
                placeholder="Ex: 50.00 (R$)"
                min="0"
                step="0.01"
                disabled={isSavingLimit}
                className="flex-1"
              />
              <Select value={editPeriod} onValueChange={setEditPeriod} disabled={isSavingLimit}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button

            onClick={handleUpdateLimit}
            disabled={isSavingLimit}
            className="w-full"
          >
            {isSavingLimit ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default APIKeys;
